import { Phone, ShieldCheck, Clock, Lock } from "lucide-react";
import { brand } from "@/data/site/brand";

interface ServiceLeadFormProps {
  /** Service slug used as the lead source / "taxIssue" tag. */
  source: string;
  /** Visible label for the service the user is requesting help with. */
  serviceLabel: string;
}

/**
 * Sticky right-rail call card for service detail pages. Phone is the only
 * conversion path — callback forms are intentionally omitted.
 */
export const ServiceLeadForm = ({ serviceLabel }: ServiceLeadFormProps) => {
  return (
    <aside className="lg:sticky lg:top-24 space-y-4">
      <div className="rounded-lg border border-border bg-white shadow-sm overflow-hidden">
        <div className="bg-gradient-navy text-white px-5 py-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold mb-1">
            Free Case Review
          </p>
          <h3 className="font-heading text-lg font-bold leading-tight">
            Talk to a CPA about your {serviceLabel.toLowerCase()} case.
          </h3>
        </div>

        <div className="p-5">
          <a
            href={`tel:${brand.phoneTel}`}
            className="flex items-center justify-center gap-2 w-full bg-gold hover:bg-gold-dark text-navy font-bold py-3 rounded transition-colors"
          >
            <Phone className="w-4 h-4" /> Call {brand.phone}
          </a>
          <p className="text-[11px] text-muted-foreground text-center mt-2">
            {brand.hours} · Avg. answer under 30 seconds
          </p>

          <ul className="mt-4 space-y-1.5 text-[11px] text-muted-foreground">
            <li className="flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3 text-gold" /> Reviewed by a credentialed CPA
            </li>
            <li className="flex items-center gap-1.5">
              <Lock className="w-3 h-3 text-gold" /> Confidential — protected by IRS §7216
            </li>
            <li className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-gold" /> Same-day answers when you call
            </li>
          </ul>
        </div>
      </div>


      <div className="rounded-lg border border-border bg-secondary/40 p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold mb-3">
          What happens after you call
        </p>
        <ol className="space-y-3 text-[13px] text-foreground/85">
          <li className="flex gap-3">
            <span className="font-heading font-bold text-gold leading-none">01</span>
            <span>15-minute confidential review with a CPA — no sales pitch.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-heading font-bold text-gold leading-none">02</span>
            <span>Flat-fee quote tied to your specific case — no surprises.</span>
          </li>
          <li className="flex gap-3">
            <span className="font-heading font-bold text-gold leading-none">03</span>
            <span>Power of Attorney filed within 24 hours so the IRS contacts us, not you.</span>
          </li>
        </ol>
      </div>
    </aside>
  );
};
